import { ActionKind } from '@dynamic/ui';
import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router';

import { Item, MediaType } from '#/client/models';
import { isOneError } from '#/errors/oneError';
import {
  isGeoRestrictionError,
  isPlaybackError,
  isUserAccessError,
  UserAccessErrorReason,
} from '#/errors/serviceErrors';
import { useActions } from '#/hooks/actions/useActions';
import { useConfig } from '#/hooks/useConfig';
import { useNavigation } from '#/hooks/useNavigation';
import { AppStatus } from '#/service/constants';
import { useAuthStore } from '#/store/auth/auth.store';
import { playerStore } from '#/store/player/player.store';
import { tb } from '#/store/translation/translation.store';

import { StatusProps } from './Status';

const getCurrentVideoParams = (): [
  string | undefined,
  MediaType | undefined,
  Item | undefined,
] => {
  const item = playerStore.playerResources?.resource;
  const resourceId = item?.id;
  const mediaType = item?.type;

  return [resourceId, mediaType, item];
};

let previouslyFetchedWithEntitlements: string[] | undefined | null = null;

/**
 * Reloads the page if the user's entitlements have changed when Entitlements error is encountered.
 * @param error - The error that triggered the fallback
 */
const useEntitlementsPageReload = (error?: Error) => {
  const { isInitialised: isAuthStoreInitialized, entitlements } =
    useAuthStore();

  const { reloadPage } = useNavigation('Fallback - Entitlements Reload');

  useEffect(() => {
    if (!isUserAccessError(error, UserAccessErrorReason.ENTITLEMENT)) {
      return;
    }

    const isSameEntitlements =
      previouslyFetchedWithEntitlements === entitlements.grantedEntitlementTags;

    if (
      !previouslyFetchedWithEntitlements ||
      !isAuthStoreInitialized ||
      isSameEntitlements
    ) {
      return;
    }
    reloadPage();
  }, [
    reloadPage,
    entitlements.grantedEntitlementTags,
    isAuthStoreInitialized,
    error,
  ]);

  return useCallback(() => {
    previouslyFetchedWithEntitlements = entitlements.grantedEntitlementTags;
  }, [entitlements.grantedEntitlementTags]);
};

export const useFallback = (error?: Error): StatusProps => {
  const { pathname } = useLocation();
  const { goBack, goHome, reloadPage } = useNavigation('Fallback');
  const { actions } = useActions();

  const isFromPlayback = pathname.startsWith('/playback/');
  const isInAppEnabled = useConfig().featureConfig.purchase?.inApp?.enabled;

  const updateEntitlements = useEntitlementsPageReload(error);

  const commonProps = {
    status: AppStatus.AccessDenied,
    statusTitle: error?.name,
    statusMessage: error?.message,
  };

  switch (true) {
    // one error cases - back button
    case isOneError(error):
      return {
        onDismiss: goBack,
        error,
      };
    // display player specific errors - now only from video player
    case isPlaybackError(error):
      return {
        ...commonProps,
        onDismiss: goBack,
        status: AppStatus.Playback,
        statusTitle: tb('error.generic.title'),
      };
    // app is geoblocked - no dismiss because there's nowhere to go
    // playback is geoblocked - back button
    case isGeoRestrictionError(error):
      return {
        ...commonProps,
        status: AppStatus.GeoBlocked,
        ...(isFromPlayback && {
          dismissMessage: tb('button.goBack'),
          onDismiss: goBack,
        }),
      };
    // authentication needed - login button
    case isUserAccessError(error, UserAccessErrorReason.AUTHENTICATION):
      return {
        ...commonProps,
        dismissMessage: tb('button.login'),
        onDismiss: () => {
          actions[ActionKind.Login]?.();
        },
        onCancel: goBack,
      };
    // entitlement needed - subscribe button
    case isUserAccessError(error, UserAccessErrorReason.ENTITLEMENT):
      return isInAppEnabled
        ? {
            ...commonProps,
            dismissMessage: tb('button.subscribe'),
            onDismiss: () => {
              updateEntitlements();
              const params = getCurrentVideoParams();
              actions[ActionKind.Purchase]?.(...params);
            },
            onCancel: goBack,
          }
        : {
            ...commonProps,
            dismissMessage: tb('button.dismiss'),
            onDismiss: goHome,
          };
    // max concurrent playbacks reached or currently not playable content or parental lock in playback - back button
    case isUserAccessError(error, UserAccessErrorReason.DEVICE_CONCURRENCY) ||
      isUserAccessError(error, UserAccessErrorReason.NOT_PLAYABLE) ||
      (isUserAccessError(error, UserAccessErrorReason.PARENTAL_LOCK) &&
        isFromPlayback):
      return {
        ...commonProps,
        status: AppStatus.Error,
        onDismiss: goBack,
      };
    // any other user access error - reload button
    case isUserAccessError(error):
      return {
        ...commonProps,
        onDismiss: reloadPage,
      };
    default:
      return {
        fatalError: true,
        error,
        onDismiss: goBack,
      };
  }
};
